// hooks/usePostHydration.ts — fetch a single post into postStore by id (B4a retro-fix).
//
// Growth slice (1.21.60): GET /api/posts/<id>/ is now public — a signed-out visitor
// following a shared post link must hydrate the real post too, not just a signed-in one.

import { useEffect, useRef } from 'react';
import { postsService } from '@/core/services/postsService';

/**
 * @param id         the raw route param — a legacy id OR a share code.
 * @param resolvedId the id of the post `id` turned out to name, once the store
 *                   can say (`usePost(id)?.id`). Always the LEGACY id.
 *
 * 1.24.00 D1.2: this fetches once per POST, not once per ident. `/post/post-1`
 * and `/post/XG886741794180` are the same post, and PostDetailPage now rewrites
 * the first into the second on arrival — with the plain `[id]` effect this hook
 * used to carry, that rewrite cost a second, identical round trip on every post
 * anybody opened by a legacy id.
 *
 * It deliberately does NOT skip the fetch just because the store already holds
 * the post. Arriving from a feed, the store holds a LIST copy; refreshing it is
 * the whole point of hydration and must not be "optimised" away.
 */
export function usePostHydration(id: string, resolvedId?: string): void {
  const fetched = useRef<string | null>(null);
  useEffect(() => {
    if (!id) return;
    // Key on the RESOLVED post when we have one, so every ident for one post
    // collapses to a single key across the canonical rewrite.
    const key = resolvedId ?? id;
    // Already asked, under this post's resolved name. Nothing to do.
    if (fetched.current === key) return;
    if (fetched.current === id) {
      // Already asked, under the ident still in the URL — the store just could
      // not name the post yet when we asked. PROMOTE the slot to the resolved
      // key rather than only returning: a post can be reached by THREE names
      // (its retired share code, its legacy id, its current share code), and a
      // retired-code arrival meets all three in sequence — retired in the URL,
      // legacy once the fetch lands, current after the canonical rewrite. Left
      // holding the retired ident, this guard would read that rewritten address
      // as a stranger and ask the server for the same post a second time, on
      // exactly the old-link path this release exists to keep alive.
      fetched.current = key;
      return;
    }
    fetched.current = key;
    void postsService.getPost(id).catch(() => {});
  }, [id, resolvedId]);
}
